/**
 * Mapping of the cinema website's internal analytics event names onto the tracking systems a
 * cinema can connect in the cinema app (Google Analytics 4, Meta Pixel, Matomo).
 *
 * Only events raised through `useAnalytics()` reach these maps — they run inside the visitor's
 * browser, where the tracker scripts live. Events raised from server actions or server components
 * never reach a pixel and are therefore deliberately absent here.
 *
 * The maps are intentionally incomplete: an event without an entry still arrives at every
 * connected system under its generic fallback (see the trackers in `utils/Providers.tsx`), so
 * adding an event does not require touching this file. Entries exist where the generic name would
 * lose meaning — mainly to reach the platforms' *standard* event names, which are the only ones
 * their reporting and ad optimisation understand.
 *
 * The public reference derived from this file lives in the docs app
 * (`apps/docs/docs/guides/website-tracking.mdx`); keep the two in sync.
 */

/**
 * Narrows an event property to something safe to send as a single string dimension. Event data is
 * `Record<string, unknown>`, and stringifying an object would yield `[object Object]`, so anything
 * non-scalar is dropped instead.
 */
function scalarName(value: unknown): string | undefined {
    return typeof value === 'string' || typeof value === 'number' ? String(value) : undefined;
}

/**
 * Internal event name → GA4 event name.
 * GA4 custom event names must be snake_case and at most 40 characters. `share`, `select_content`,
 * `view_item`, `begin_checkout`, `select_promotion` and `video_start` are GA4 recommended events.
 */
export const GA4_EVENT_MAP: Record<string, string> = {
    'Ticket Button Clicked': 'begin_checkout',
    'Showing Info Modal Opened': 'view_item',
    'Movie Poster Clicked': 'select_content',
    'Voucher Link Clicked': 'select_promotion',
    'Card Video Played': 'video_start',
    // Both share actions report as GA4's recommended `share`; they stay separable through the
    // `source` parameter each call site sends.
    'Share Page Link Clicked': 'share',
    'Share Showing Detail Link Clicked': 'share',
    'Program Filter Changed': 'search',
    'Program Sorting Changed': 'program_sorting_changed',
    'Program List Date Selected': 'program_date_selected',
    'Cinema Room Details Opened': 'view_cinema_room',
    'Movie Description Text Expanded': 'description_expanded',
    'FAQ Question Opened': 'faq_opened',
    'Navigation Item Clicked': 'navigation_click',
    'Home Card Clicked': 'home_card_click',
    'Special Item Clicked': 'special_click',
    'Highlight Item Clicked': 'highlight_click',
    'Book Cinema Clicked': 'rent_cinema_click',
    'Job Card Clicked': 'job_click',
    'App Banner Clicked': 'app_banner_click'
};

/**
 * Meta Pixel event configuration.
 * Standard events (ViewContent, InitiateCheckout, Lead, …) are recognised by Meta's ad
 * optimisation; everything else goes through `fbq('trackCustom', …)` and is tracked but not
 * optimisable.
 */
type MetaPixelEventConfig = {
    eventName: string;
    isStandard: boolean;
    mapParams?: (data: Record<string, unknown>) => Record<string, unknown>;
};

export const META_PIXEL_EVENT_MAP: Record<string, MetaPixelEventConfig> = {
    'Ticket Button Clicked': {
        eventName: 'InitiateCheckout',
        isStandard: true,
        mapParams: (data) => ({
            content_name: (data.showingName as string) || (data.contentName as string),
            content_category: 'Ticket',
            content_type: 'product',
            content_ids: scalarName(data.showingId) ? [scalarName(data.showingId)] : undefined
        })
    },
    'Showing Info Modal Opened': {
        eventName: 'ViewContent',
        isStandard: true,
        mapParams: (data) => ({
            content_name: (data.showingName as string) || (data.contentName as string),
            content_category: 'Showing',
            content_type: 'product'
        })
    },
    'Movie Poster Clicked': {
        eventName: 'ViewContent',
        isStandard: true,
        mapParams: (data) => ({
            content_name: data.contentName as string,
            content_category: 'Movie',
            content_type: 'product'
        })
    },
    'Voucher Link Clicked': {
        eventName: 'ViewContent',
        isStandard: true,
        mapParams: (data) => ({
            content_name: (data.cinemaName as string) ?? '',
            content_category: 'Voucher'
        })
    },
    'Card Video Played': {
        eventName: 'ViewContent',
        isStandard: true,
        mapParams: (data) => ({
            content_name: (data.cardVideo as string) ?? '',
            content_category: 'Video',
            content_type: 'video'
        })
    },
    'Program Filter Changed': {
        eventName: 'Search',
        isStandard: true,
        mapParams: (data) => ({
            search_string: (data.filter as string) ?? '',
            content_category: 'Program'
        })
    },
    'Program Sorting Changed': { eventName: 'ProgramSortingChanged', isStandard: false },
    'Program List Date Selected': { eventName: 'ProgramListDateSelected', isStandard: false },
    'Share Page Link Clicked': { eventName: 'SharePage', isStandard: false },
    'Share Showing Detail Link Clicked': { eventName: 'ShareShowing', isStandard: false },
    'Cinema Room Details Opened': { eventName: 'CinemaRoomDetailsOpened', isStandard: false },
    'Movie Description Text Expanded': { eventName: 'DescriptionExpanded', isStandard: false },
    'FAQ Question Opened': { eventName: 'FaqOpened', isStandard: false },
    'Navigation Item Clicked': { eventName: 'NavigationClicked', isStandard: false },
    'Home Card Clicked': { eventName: 'HomeCardClicked', isStandard: false },
    'Special Item Clicked': { eventName: 'SpecialClicked', isStandard: false },
    'Highlight Item Clicked': { eventName: 'HighlightClicked', isStandard: false },
    'Book Cinema Clicked': {
        eventName: 'Lead',
        isStandard: true,
        mapParams: (data) => ({
            content_name: (data.cinemaName as string) ?? '',
            content_category: 'Rent a Cinema'
        })
    },
    'Job Card Clicked': { eventName: 'JobClicked', isStandard: false },
    'App Banner Clicked': { eventName: 'AppBannerClicked', isStandard: false }
};

/**
 * Matomo event configuration — Matomo's `trackEvent` takes a fixed
 * (category, action, name) triple rather than free-form properties.
 */
type MatomoEventConfig = {
    category: string;
    action: string;
    /** Picks the event's "name" dimension out of the event data. */
    mapName?: (data: Record<string, unknown>) => string | undefined;
};

const showingName = (data: Record<string, unknown>) =>
    (data.showingName as string) || (data.contentName as string) || undefined;

export const MATOMO_EVENT_MAP: Record<string, MatomoEventConfig> = {
    'Ticket Button Clicked': { category: 'Ticket', action: 'Ticket Button Clicked', mapName: showingName },
    'Showing Info Modal Opened': { category: 'Program', action: 'Showing Info Opened', mapName: showingName },
    'Movie Poster Clicked': {
        category: 'Program',
        action: 'Movie Poster Clicked',
        mapName: (data) => data.contentName as string | undefined
    },
    'Program Filter Changed': {
        category: 'Program',
        action: 'Filter Changed',
        mapName: (data) => data.filter as string | undefined
    },
    'Program Sorting Changed': {
        category: 'Program',
        action: 'Sorting Changed',
        mapName: (data) => data.sorting as string | undefined
    },
    'Program List Date Selected': {
        category: 'Program',
        action: 'Date Selected',
        mapName: (data) => data.date as string | undefined
    },
    'Movie Description Text Expanded': {
        category: 'Program',
        action: 'Description Expanded',
        mapName: (data) => scalarName(data.movieId)
    },
    'Share Page Link Clicked': {
        category: 'Share',
        action: 'Page Link Copied',
        mapName: (data) => data.source as string | undefined
    },
    'Share Showing Detail Link Clicked': {
        category: 'Share',
        action: 'Showing Link Copied',
        mapName: (data) => data.source as string | undefined
    },
    'Voucher Link Clicked': { category: 'Shop', action: 'Voucher Link Clicked' },
    'Card Video Played': {
        category: 'Content',
        action: 'Card Video Played',
        mapName: (data) => data.cardVideo as string | undefined
    },
    'Cinema Room Details Opened': {
        category: 'Cinema',
        action: 'Room Details Opened',
        mapName: (data) => data.cinemaRoomName as string | undefined
    },
    'FAQ Question Opened': {
        category: 'Info',
        action: 'FAQ Opened',
        mapName: (data) => data.questionText as string | undefined
    },
    'Navigation Item Clicked': {
        category: 'Navigation',
        action: 'Item Clicked',
        mapName: (data) => data.item as string | undefined
    },
    'Home Card Clicked': {
        category: 'Home',
        action: 'Card Clicked',
        mapName: (data) => data.title as string | undefined
    },
    'Special Item Clicked': {
        category: 'Home',
        action: 'Special Clicked',
        mapName: (data) => data.name as string | undefined
    },
    'Highlight Item Clicked': {
        category: 'Home',
        action: 'Highlight Clicked',
        mapName: (data) => data.title as string | undefined
    },
    'Book Cinema Clicked': { category: 'Home', action: 'Rent a Cinema Clicked' },
    'Job Card Clicked': { category: 'Home', action: 'Job Clicked' },
    'App Banner Clicked': { category: 'Home', action: 'App Banner Clicked' }
};

/**
 * Convert an event name to snake_case for the GA4 fallback.
 * E.g. `OT: Seat Selected` → `ot_seat_selected`.
 */
export function toSnakeCase(value: string): string {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '')
        .slice(0, 40);
}
